import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
} from 'recharts'

interface TrendDataPoint {
  label: string
  value: number
}

interface TrendAreaChartProps {
  data: TrendDataPoint[]
  height?: number
  color?: string
  valuePrefix?: string
  onPointClick?: (point: TrendDataPoint, index: number) => void
  className?: string
}

function formatCompact(n: number): string {
  if (Math.abs(n) >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`
  if (Math.abs(n) >= 1_000) return `${(n / 1_000).toFixed(0)}K`
  return n.toLocaleString()
}

function CustomTooltip({
  active,
  payload,
  label,
  valuePrefix = '',
}: any) {
  if (!active || !payload?.length) return null
  return (
    <div
      style={{
        background: '#FFFFFF',
        border: '1px solid #E2E8F0',
        borderRadius: 6,
        padding: '8px 12px',
        fontSize: 12,
        boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
      }}
    >
      <div style={{ color: '#94A3B8', marginBottom: 2 }}>{label}</div>
      <div style={{ color: '#0F172A', fontWeight: 600 }}>
        {valuePrefix}
        {payload[0].value.toLocaleString()}
      </div>
    </div>
  )
}

export function TrendAreaChart({
  data,
  height = 200,
  color = '#2563EB',
  valuePrefix = '',
  onPointClick,
  className = '',
}: TrendAreaChartProps) {
  if (!data || data.length === 0) return null

  const gradientId = `trend-gradient-${color.replace('#', '')}`

  return (
    <div className={className}>
      <ResponsiveContainer width="100%" height={height}>
        <AreaChart
          data={data}
          onClick={
            onPointClick
              ? (e: any) => {
                  if (e?.activePayload?.[0]) {
                    const idx = e.activeTooltipIndex
                    onPointClick(data[idx], idx)
                  }
                }
              : undefined
          }
        >
          <defs>
            <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={color} stopOpacity={0.12} />
              <stop offset="100%" stopColor={color} stopOpacity={0} />
            </linearGradient>
          </defs>

          <XAxis
            dataKey="label"
            tick={{ fontSize: 11, fill: '#94A3B8' }}
            axisLine={{ stroke: '#E2E8F0' }}
            tickLine={false}
          />
          <YAxis
            tick={{ fontSize: 11, fill: '#94A3B8' }}
            axisLine={false}
            tickLine={false}
            width={50}
            tickFormatter={formatCompact}
          />
          <Tooltip
            content={<CustomTooltip valuePrefix={valuePrefix} />}
            cursor={{ stroke: '#E2E8F0', strokeDasharray: '4 4' }}
          />
          <Area
            type="monotone"
            dataKey="value"
            stroke={color}
            strokeWidth={2}
            fill={`url(#${gradientId})`}
            dot={false}
            activeDot={{
              r: 4,
              fill: '#FFFFFF',
              stroke: color,
              strokeWidth: 2,
            }}
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  )
}

export default TrendAreaChart
